import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { getThemeColors } from '../../utils/theme';
import API_ENDPOINTS from '../../config';
import { authenticatedFetch } from '../../utils/api';
import logger from '../../utils/logger';

export default function AdminAuditLogs() {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);
  const [filters, setFilters] = useState({
    companyId: '',
    action: '',
    startDate: '',
    endDate: '',
  });
  const [companies, setCompanies] = useState([]);
  const [user, setUser] = useState(null);

  // Fetch user info to check if system admin
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await authenticatedFetch(API_ENDPOINTS.PROFILE, {
          method: 'GET',
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data.user || data);
        }
      } catch (error) {
        logger.error('Error fetching user:', error);
      }
    };
    fetchUser();
  }, []);

  // Fetch companies for filter (only for system admins)
  useEffect(() => {
    const fetchCompanies = async () => {
      if (user && user.role === 'admin') {
        try {
          const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/admin/companies?limit=1000`, {
            method: 'GET',
          });
          if (response.ok) {
            const data = await response.json();
            setCompanies(data.companies || []);
          }
        } catch (error) {
          logger.error('Error fetching companies:', error);
        }
      }
    };
    fetchCompanies();
  }, [user]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      if (filters.companyId) {
        params.append('companyId', filters.companyId);
      }
      if (filters.action) {
        params.append('action', filters.action);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }

      const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/audit/logs?${params}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }

      const data = await response.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) {
      logger.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [offset, limit, filters]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setOffset(0); // Reset to first page when filters change
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.companyId) {
        params.append('companyId', filters.companyId);
      }
      if (filters.action) {
        params.append('action', filters.action);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }

      const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/audit/export?${params}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to export audit logs');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      logger.error('Error exporting audit logs:', error);
      if (error.message !== 'Unauthorized - redirected to login') {
        alert(t('admin.auditLogs.exportError'));
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const localeMap = {
        'sr': 'sr-RS',
        'zh': 'zh-CN',
        'en': 'en-US'
      };
      const locale = localeMap[i18n.language] || 'en-US';
      return date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatAction = (action) => {
    const actionMap = {
      'member_invited': t('admin.auditLogs.actionMemberInvited'),
      'invitation_accepted': t('admin.auditLogs.actionInvitationAccepted'),
      'member_removed': t('admin.auditLogs.actionMemberRemoved'),
      'role_changed': t('admin.auditLogs.actionRoleChanged'),
    };
    return actionMap[action] || action;
  };

  const formatUser = (user) => {
    if (!user) return '-';
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
    }
    return user.email || '-';
  };

  const actionTypes = [
    { value: '', label: t('admin.auditLogs.allActions') },
    { value: 'member_invited', label: t('admin.auditLogs.actionMemberInvited') },
    { value: 'invitation_accepted', label: t('admin.auditLogs.actionInvitationAccepted') },
    { value: 'member_removed', label: t('admin.auditLogs.actionMemberRemoved') },
    { value: 'role_changed', label: t('admin.auditLogs.actionRoleChanged') },
  ];

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '2rem', fontWeight: 700, color: colors.text }}>
          📋 {t('admin.auditLogs.title')}
        </h1>
        <p style={{ margin: 0, color: colors.textSecondary }}>
          {t('admin.auditLogs.subtitle')}
        </p>
      </div>

      {/* Filters */}
      <div style={{
        background: colors.card,
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          {user && user.role === 'admin' && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                {t('admin.auditLogs.organization')}
              </label>
              <select
                value={filters.companyId}
                onChange={(e) => handleFilterChange('companyId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="">{t('admin.auditLogs.allOrganizations')}</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 600, color: colors.text }}>
              {t('admin.auditLogs.filterByAction')}
            </label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
              }}
            >
              {actionTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 600, color: colors.text }}>
              {t('admin.auditLogs.fromDate')}
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 600, color: colors.text }}>
              {t('admin.auditLogs.toDate')}
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              setFilters({
                companyId: '',
                action: '',
                startDate: '',
                endDate: '',
              });
              setOffset(0);
            }}
            style={{
              padding: '8px 16px',
              background: colors.backgroundLight,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              color: colors.text,
            }}
          >
            {t('common.resetFilters')}
          </button>
          <button
            onClick={handleExport}
            style={{
              padding: '8px 16px',
              background: colors.primary,
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              color: 'white',
              fontWeight: 600,
            }}
          >
            📥 {t('admin.auditLogs.exportCSV')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{
        background: colors.card,
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: colors.textSecondary }}>
            {t('admin.auditLogs.loading')}
          </div>
        ) : logs.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: colors.textSecondary }}>
            {t('admin.auditLogs.noLogs')}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.backgroundLight, borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>{t('admin.auditLogs.dateTime')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>{t('admin.auditLogs.organization')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>{t('admin.auditLogs.action')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>{t('admin.auditLogs.executedBy')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>{t('admin.auditLogs.targetUser')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>{t('admin.auditLogs.details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px', fontSize: '14px', color: colors.text }}>
                        {formatDate(log.createdAt)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: colors.text }}>
                        {log.company?.name || '-'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: colors.text }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: log.action === 'member_removed' ? '#fee2e2' : log.action === 'role_changed' ? '#fef3c7' : '#dbeafe',
                          color: log.action === 'member_removed' ? '#dc2626' : log.action === 'role_changed' ? '#d97706' : '#2563eb',
                        }}>
                          {formatAction(log.action)}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: colors.text }}>
                        {formatUser(log.user)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: colors.text }}>
                        {formatUser(log.targetUser)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: colors.text }}>
                        {log.metadata ? (
                          <details>
                            <summary style={{ cursor: 'pointer', color: '#2563eb' }}>{t('common.show')}</summary>
                            <pre style={{
                              marginTop: '8px',
                              padding: '8px',
                              background: colors.backgroundLight,
                              borderRadius: '4px',
                              fontSize: '12px',
                              overflow: 'auto',
                            }}>
                              {JSON.stringify(log.metadata, null, 2)}
                            </pre>
                          </details>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{
              padding: '1rem',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ fontSize: '14px', color: colors.textSecondary }}>
                {t('admin.auditLogs.showingLogs', {
                  from: offset + 1,
                  to: Math.min(offset + limit, total),
                  total: total
                })}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  style={{
                    padding: '6px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    background: offset === 0 ? '#f3f4f6' : '#fff',
                    cursor: offset === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    color: offset === 0 ? '#9ca3af' : '#374151',
                  }}
                >
                  {t('common.previous')}
                </button>
                <span style={{ padding: '6px 12px', fontSize: '14px', color: colors.text }}>
                  {t('admin.auditLogs.pageOf', { page: currentPage, pages: totalPages })}
                </span>
                <button
                  onClick={() => setOffset(Math.min((totalPages - 1) * limit, offset + limit))}
                  disabled={offset + limit >= total}
                  style={{
                    padding: '6px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    background: offset + limit >= total ? '#f3f4f6' : '#fff',
                    cursor: offset + limit >= total ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    color: offset + limit >= total ? '#9ca3af' : '#374151',
                  }}
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
